import type { ReactNode } from 'react'
import type {
  ProblemAttemptDto,
  ProblemHistoryDto,
} from '@/lib/coaching/problem-history-query'
import { describeAttempt, summarizeHistory } from '../_lib/history-log'
import { HEADING_CLASS } from './section-heading'

/**
 * What this client has already done with this problem (LG-360 §5).
 *
 * Coach-only: it renders in the admin modal and has no counterpart in the
 * client's task dialog. Telling a client "you have failed this three times"
 * over their own homework is not the same product decision as telling their
 * coach, and the ticket only asks for the second.
 *
 * A card with no catalog problem behind it renders nothing rather than "no
 * history" — that is decided by the caller, which does not mount this
 * component at all in that case: there is no problem to have a history of,
 * a different claim from a problem the client has not seen.
 */
export function ProblemHistorySection(props: {
  history: ProblemHistoryDto
  attempts: readonly ProblemAttemptDto[]
  pending: boolean
  /**
   * True when the caller's fetch for this history failed. Rendered as a
   * short `role="status"` line, the same idiom `CompletionControl` and
   * `NoteEditor` already use for their own write failures — silence here
   * would be indistinguishable from a card with no problem behind it at
   * all, which the caller never even mounts this component for.
   */
  error?: boolean
}): ReactNode {
  const { history, attempts, pending, error = false } = props

  if (pending) {
    return (
      <section className="flex flex-col gap-1.5">
        <h3 className={HEADING_CLASS}>History with this client</h3>
        {/* A single line, not a skeleton: a skeleton sized to the eventual
            content would shift the modal's height the moment real data
            replaces it, and the modal has nothing here to size one against
            in the meantime. */}
        <p
          aria-busy="true"
          className="text-[11.5px] text-(--cd-text-faint)"
          role="status"
        >
          Loading…
        </p>
      </section>
    )
  }

  if (error) {
    return (
      <section className="flex flex-col gap-1.5">
        <h3 className={HEADING_CLASS}>History with this client</h3>
        <p
          aria-live="polite"
          className="text-[11.5px] text-(--cd-red)"
          role="status"
        >
          Could not load this client's history.
        </p>
      </section>
    )
  }

  const now = new Date()
  const { headline, detail } = summarizeHistory(history, now)

  return (
    <section className="flex flex-col gap-1.5">
      <h3 className={HEADING_CLASS}>History with this client</h3>
      <p className="text-[11.5px] font-semibold text-(--cd-text)">{headline}</p>
      {detail !== null ? (
        <p className="text-[10px] text-(--cd-text-faint)">{detail}</p>
      ) : null}
      {attempts.length > 0 ? (
        <ul className="flex flex-col gap-1 rounded-lg border border-(--cd-border-2) bg-(--cd-inset) px-2.5 py-2">
          {attempts.map((attempt, index) => (
            <li
              className="text-[10.5px] text-(--cd-text-muted)"
              key={`${attempt.provenance}-${attempt.at.toISOString()}-${index}`}
            >
              {describeAttempt(attempt, now)}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
